/**
 * ShippingVendorReceiptCard.tsx
 *
 * "Shipping and Vendor Receipt" card on Repair / Replace / Return tickets. It loads with the ticket
 * and shows the return-to-vendor shipment and, depending on the viewer:
 *   - the Verify Receipt by SKU form (vendor, or Sustain on the vendor's behalf)
 *   - the vendor's confirmation or the open discrepancy with its resolution actions
 *   - the RMA inspection and warranty disposition for each serialized unit
 * Everything the viewer may do is decided by the backend and returned with the shipment.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useToast } from "../../../components/LhToastContainer";
import { ShipmentBundle, ShipmentRecord, getShipmentBundle, submitSiteTracking } from "../../../api/shipmentApi";
import SingleSelectDropdown from "./SingleSelectDropdown";
import { SHIPMENT_CARRIERS, toShipmentCarrier } from "../util/shipmentCarrier";
import { PackingSlipResponse, getPackingSlip } from "../../../api/packingSlipApi";
import { countUnits, formatIsoDate, shortVendorName } from "../util/shipmentReceiptUtils";
import { formatDateWithTimezone } from "../util/dateFormatter";
import ShipmentReceiptForm from "./ShipmentReceiptForm";
import ShipmentDiscrepancyPanel from "./ShipmentDiscrepancyPanel";
import RmaInspectionSection from "./RmaInspectionSection";

export interface ShippingVendorReceiptCardProps {
  /** Numeric backend ticket ID (ticket.backendTicketId). */
  ticketId?: number | string | null;
  /** Bump to reload after a change made elsewhere on the ticket. */
  refreshKey?: number;
  /** Called after any successful mutation so the parent can refetch the ticket. */
  onMutated?: () => void;
  /** Opens the Packing Slip screen for this ticket. The button is hidden when omitted. */
  onOpenPackingSlip?: () => void;
}

// ---------------------------------------------------------------------------
// Leg status badge
// ---------------------------------------------------------------------------

interface BadgeStyle { label: string; bg: string; fg: string }

const legBadge = (shipment: ShipmentRecord): BadgeStyle => {
  switch (shipment.status) {
    case "Pending Approval": return { label: "Waiting on User", bg: "#FFE9D1", fg: "#CC6E16" };
    case "In Transit": return { label: "In Transit", bg: "#DAEEFA", fg: "#005CBC" };
    case "Delivered": return { label: "Delivered", bg: "#DAEEFA", fg: "#0078A7" };
    case "In Variance": return { label: "In Variance", bg: "#FEE4E2", fg: "#B42318" };
    case "Reconciled": return { label: "Received", bg: "#E1E3FD", fg: "#7871E2" };
    default: return { label: shipment.status, bg: "#E9E9E9", fg: "#6B7280" };
  }
};

// ---------------------------------------------------------------------------
// Site tracking form
// ---------------------------------------------------------------------------

const TrackingForm: React.FC<{
  ticketId: number;
  initialCarrier?: string | null;
  onSaved: (message: string) => void;
}> = ({ ticketId, initialCarrier, onSaved }) => {
  const [carrier, setCarrier] = useState(toShipmentCarrier(initialCarrier));
  const [tracking, setTracking] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!carrier) { setFormError("Select a carrier."); return; }
    if (!tracking.trim()) { setFormError("Enter the carrier tracking number."); return; }
    setFormError(null);
    setSaving(true);
    const outcome = await submitSiteTracking(ticketId, { carrier, trackingNumber: tracking.trim() });
    setSaving(false);
    if (outcome.error) { setFormError(outcome.error); return; }
    onSaved(outcome.data?.message || "Tracking number saved.");
  };

  return (
    <View style={s.trackBox}>
      <Text style={s.trackTitle}>Add carrier tracking</Text>
      <Text style={s.trackHelp}>The vendor can verify receipt once a carrier tracking number is on file.</Text>
      <View style={s.trackRow}>
        <View style={s.trackCol}>
          <SingleSelectDropdown label="Carrier" value={carrier} options={SHIPMENT_CARRIERS} emptyLabel="Select carrier" allowEmpty={false} onChange={setCarrier} />
        </View>
        <View style={s.trackCol}>
          <Text style={s.trackLabel}>Tracking number</Text>
          <TextInput style={s.trackInput} value={tracking} onChangeText={setTracking} placeholder="Enter tracking number" placeholderTextColor="#9099AA" autoCapitalize="characters" />
        </View>
      </View>
      {formError ? <Text style={s.trackError}>{formError}</Text> : null}
      <Pressable style={({ pressed }) => [s.trackBtn, (pressed || saving) && s.pressed]} onPress={handleSave} disabled={saving}>
        <Text style={s.trackBtnText}>{saving ? "Saving..." : "Save Tracking"}</Text>
      </Pressable>
    </View>
  );
};

// ---------------------------------------------------------------------------
// Return-to-vendor leg
// ---------------------------------------------------------------------------

const ReturnLeg: React.FC<{
  ticketId: number;
  bundle: ShipmentBundle;
  slip: PackingSlipResponse | null;
  onOpenPackingSlip?: () => void;
  onChanged: (message: string) => void;
}> = ({ ticketId, bundle, slip, onOpenPackingSlip, onChanged }) => {
  const shipment = bundle.shipment as ShipmentRecord;
  const receipt = bundle.receipt ?? null;
  const inspections = bundle.inspections ?? null;
  const badge = legBadge(shipment);

  const label = slip?.slipNumber || `Shipment ${shipment.shipmentId}`;
  const skuCount = receipt?.skus?.length ?? slip?.lines?.length;
  const unitCount = receipt?.skus?.length ? countUnits(receipt.skus) : slip?.totalUnits;

  const dest = shipment.destinationSnapshot;
  const address = [
    dest?.attention,
    dest?.addressLine1,
    dest?.addressLine2,
    [dest?.city, [dest?.state, dest?.postalCode].filter(Boolean).join(" ")].filter(Boolean).join(", "),
  ].filter(Boolean).join("\n");

  const hasTracking = !!shipment.trackingNumber;
  const pendingApproval = shipment.status === "Pending Approval";
  const result = receipt?.result;
  const reconciled = receipt?.state === "RECONCILED";
  const anyRecorded = (inspections?.units ?? []).some((u) => !!u.recorded);

  return (
    <View style={s.legCard}>
      <View style={s.legHeader}>
        <View style={{ flex: 1, minWidth: 180 }}>
          <Text style={s.legTitle}>{label} to {shipment.vendorName}</Text>
          {unitCount != null && skuCount != null ? (
            <Text style={s.legMeta}>{unitCount} unit(s) across {skuCount} SKU(s)</Text>
          ) : null}
        </View>
        <View style={[s.statusBadge, { backgroundColor: badge.bg }]}>
          <View style={[s.statusDot, { backgroundColor: badge.fg }]} />
          <Text style={[s.statusText, { color: badge.fg }]}>{badge.label}</Text>
        </View>
      </View>

      <View style={s.kvGrid}>
        {address ? (
          <View style={s.kv}><Text style={s.kvLabel}>SHIP TO</Text><Text style={s.kvValue}>{address}</Text></View>
        ) : null}
        <View style={s.kv}>
          <Text style={s.kvLabel}>CARRIER AND TRACKING</Text>
          {hasTracking ? (
            <Text style={[s.kvValue, s.mono]}>{[shipment.carrier, shipment.trackingNumber].filter(Boolean).join("  ")}</Text>
          ) : (
            <Text style={[s.kvValue, s.notProvided]}>Not provided</Text>
          )}
        </View>
        <View style={s.kv}>
          <Text style={s.kvLabel}>SHIP DATE</Text>
          <Text style={s.kvValue}>{shipment.shipDate ? formatIsoDate(shipment.shipDate) : "Not shipped"}</Text>
        </View>
        <View style={s.kv}>
          <Text style={s.kvLabel}>BOXES / WEIGHT</Text>
          <Text style={s.kvValue}>{shipment.boxCount ?? "-"} / {shipment.weight != null ? `${shipment.weight} lb` : "-"}</Text>
        </View>
        {shipment.carrierDeliveredAt ? (
          <View style={s.kv}>
            <Text style={s.kvLabel}>CARRIER DELIVERED</Text>
            <Text style={s.kvValue}>{formatDateWithTimezone(shipment.carrierDeliveredAt)}</Text>
            {receipt?.discrepancyWindowEndsAt ? (
              <Text style={s.kvSub}>Discrepancies are reported by {formatDateWithTimezone(receipt.discrepancyWindowEndsAt)}</Text>
            ) : null}
          </View>
        ) : null}
      </View>

      {onOpenPackingSlip ? (
        <View style={s.slipRow}>
          <Pressable style={({ pressed }) => [s.slipBtn, pressed && s.pressed]} onPress={onOpenPackingSlip}>
            <Text style={s.slipBtnText}>Packing Slip for {shortVendorName(shipment.vendorName)}</Text>
          </Pressable>
        </View>
      ) : null}

      {pendingApproval ? (
        <View style={s.pendingStrip}>
          <Text style={s.pendingText}>
            Shipping instructions are released once Warehouse Support approves this request. Do not ship hardware before approval.
          </Text>
        </View>
      ) : null}

      {!hasTracking && (!receipt || receipt.state === "NOT_AVAILABLE") ? (
        <TrackingForm ticketId={ticketId} initialCarrier={shipment.carrier} onSaved={onChanged} />
      ) : null}

      {receipt?.state === "NOT_AVAILABLE" && !pendingApproval && receipt.message ? (
        <View style={s.infoStrip}><Text style={s.infoText}>{receipt.message}</Text></View>
      ) : null}

      {receipt?.state === "SITE_MESSAGE" && receipt.message ? (
        <View style={s.infoStrip}>
          <MaterialIcons name="hourglass-empty" size={14} color="#005CBC" />
          <Text style={s.infoText}>{receipt.message}</Text>
        </View>
      ) : null}

      {receipt?.discrepancy && (receipt.state === "IN_VARIANCE" || receipt.state === "FORM") ? (
        <ShipmentDiscrepancyPanel ticketId={ticketId} receipt={receipt} onResolved={onChanged} />
      ) : null}

      {receipt?.state === "FORM" ? (
        <ShipmentReceiptForm
          key={shipment.shipmentId}
          ticketId={ticketId}
          shipmentId={shipment.shipmentId}
          receipt={receipt}
          onSubmitted={onChanged}
        />
      ) : null}

      {reconciled && result ? (
        <View style={s.confirmedBanner}>
          <MaterialIcons name="check" size={16} color="#027A48" style={{ marginTop: 1 }} />
          <View style={{ flex: 1 }}>
            <Text style={s.confirmedTitle}>
              {result.onBehalfOfVendor
                ? `Receipt recorded by ${result.confirmedBy ?? "Warehouse Support"} on behalf of ${shipment.vendorName}`
                : `Receipt confirmed by ${result.confirmedBy ?? shipment.vendorName}`}
              {" on "}{formatDateWithTimezone(result.confirmedAt)}
            </Text>
            <Text style={s.confirmedSub}>
              Condition on receipt: <Text style={{ fontWeight: "700" }}>{result.condition ?? "Good"}</Text>.{" "}
              {result.notes ?? (receipt?.skus?.length ? `All ${countUnits(receipt.skus)} unit(s) received and verified.` : "")}
            </Text>
          </View>
        </View>
      ) : null}

      {inspections && (reconciled || anyRecorded) ? (
        <RmaInspectionSection ticketId={ticketId} inspections={inspections} onRecorded={onChanged} />
      ) : null}
    </View>
  );
};

// ---------------------------------------------------------------------------
// Main card component
// ---------------------------------------------------------------------------

const ShippingVendorReceiptCard: React.FC<ShippingVendorReceiptCardProps> = ({
  ticketId, refreshKey = 0, onMutated, onOpenPackingSlip,
}) => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [bundle, setBundle] = useState<ShipmentBundle | null>(null);
  const [slip, setSlip] = useState<PackingSlipResponse | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const numericId = ticketId != null && Number.isFinite(Number(ticketId)) ? Number(ticketId) : null;
  const requestSeq = useRef(0);

  const loadData = useCallback(async (silent = false) => {
    if (numericId == null) return;
    const seq = ++requestSeq.current;
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(null);
    // The slip number only labels the card, so a failed slip call must not block the shipment.
    const [result, slipResult] = await Promise.all([getShipmentBundle(numericId), getPackingSlip(numericId)]);
    if (seq !== requestSeq.current) return;
    setLoading(false);
    setRefreshing(false);
    setForbidden(result.forbidden);
    if (result.forbidden) return;
    if (result.error) { setError(result.error); return; }
    setBundle(result.data);
    setSlip(slipResult.data);
  }, [numericId]);

  useEffect(() => {
    loadData(refreshKey > 0);
  }, [loadData, refreshKey]);

  const handleChanged = useCallback((message: string) => {
    setNotice(message);
    toast.show(message, "success");
    setRefreshing(true);
    loadData(true);
    onMutated?.();
  }, [onMutated, toast, loadData]);

  if (numericId == null || forbidden) return null;

  return (
    <View style={s.card}>
      <View style={s.cardHdr}>
        <MaterialIcons name="local-shipping" size={16} color="#374151" />
        <Text style={s.cardTitle}>Shipping and Vendor Receipt</Text>
      </View>
      <View style={s.cardBody}>
        {notice ? (
          <View style={s.noticeStrip}>
            <MaterialIcons name="check-circle" size={16} color="#027A48" />
            <Text style={s.noticeText}>{notice}</Text>
            <Pressable onPress={() => setNotice(null)} hitSlop={8}><MaterialIcons name="close" size={14} color="#027A48" /></Pressable>
          </View>
        ) : null}
        {loading ? (
          <View style={s.centred}>
            <ActivityIndicator size="small" color="#034895" />
            <Text style={s.loadingText}>Loading shipment data...</Text>
          </View>
        ) : error ? (
          <View style={s.errorWrap}>
            <MaterialIcons name="error-outline" size={18} color="#b45309" />
            <Text style={s.errorText}>{error}</Text>
            <Pressable onPress={() => loadData()} style={s.retryBtn}><Text style={s.retryText}>Retry</Text></Pressable>
          </View>
        ) : !bundle?.shipment ? (
          <View style={s.centred}>
            <MaterialIcons name="inventory" size={28} color="#d1d5db" />
            <Text style={s.emptyText}>No shipment records yet.</Text>
            <Text style={s.emptySubText}>
              {bundle?.noShipmentReason ?? "This ticket has nothing to ship to a vendor."}
            </Text>
          </View>
        ) : (
          <View>
            <Text style={s.sectionLabel}>Return to Vendor</Text>
            <ReturnLeg
              ticketId={numericId}
              bundle={bundle}
              slip={slip}
              onOpenPackingSlip={onOpenPackingSlip}
              onChanged={handleChanged}
            />
          </View>
        )}
        {refreshing && !loading ? (
          <View style={s.overlay}>
            <ActivityIndicator size="small" color="#034895" />
            <Text style={s.loadingText}>Updating shipment data...</Text>
          </View>
        ) : null}
      </View>
    </View>
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const MONO = Platform.OS === "ios" ? "Courier" : "monospace";

const s = StyleSheet.create({
  card: { backgroundColor: "#ffffff", borderRadius: 12, borderWidth: 1, borderColor: "#e5e7eb", marginTop: 10, marginBottom: 12, overflow: "hidden" as any },
  cardHdr: { flexDirection: "row", alignItems: "center", gap: 8, paddingHorizontal: 20, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: "#e5e7eb" },
  cardTitle: { fontSize: 15, fontWeight: "700", color: "#111827" },
  cardBody: { paddingHorizontal: 20, paddingTop: 14, paddingBottom: 6 },
  noticeStrip: { flexDirection: "row", alignItems: "center", gap: 8, backgroundColor: "#ECFDF3", borderWidth: 1, borderColor: "#ABEFC6", borderRadius: 8, padding: 10, marginBottom: 12 },
  noticeText: { flex: 1, fontSize: 12, fontWeight: "600", color: "#027A48" },
  sectionLabel: { fontSize: 10, fontWeight: "700", color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 },
  legCard: { backgroundColor: "#ffffff", borderRadius: 8, borderWidth: 1, borderColor: "#e5e7eb", marginBottom: 12, overflow: "hidden" as any },
  legHeader: { flexDirection: "row", alignItems: "flex-start", flexWrap: "wrap", padding: 14, gap: 10 },
  legTitle: { fontSize: 14, fontWeight: "700", color: "#111827" },
  legMeta: { fontSize: 11, color: "#6b7280", marginTop: 2 },
  statusBadge: { flexDirection: "row", alignItems: "center", gap: 5, paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6 },
  statusDot: { width: 6, height: 6, borderRadius: 3 },
  statusText: { fontSize: 11, fontWeight: "700" },
  kvGrid: { flexDirection: "row", flexWrap: "wrap", gap: 12, paddingHorizontal: 14, paddingBottom: 14 },
  kv: { minWidth: 200, flex: 1 },
  kvLabel: { fontSize: 10, fontWeight: "700", color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 },
  kvValue: { fontSize: 12, color: "#374151", lineHeight: 18 },
  kvSub: { fontSize: 11, color: "#6b7280", marginTop: 2, lineHeight: 16 },
  mono: { fontFamily: MONO },
  notProvided: { color: "#CC6E16", fontWeight: "700" },
  slipRow: { flexDirection: "row", paddingHorizontal: 14, paddingBottom: 14 },
  slipBtn: { borderWidth: 1, borderColor: "#034895", borderRadius: 100, paddingHorizontal: 14, paddingVertical: 7, backgroundColor: "#ffffff" },
  slipBtnText: { fontSize: 12, fontWeight: "700", color: "#034895" },
  pendingStrip: { marginHorizontal: 14, marginBottom: 14, backgroundColor: "#FFF6CC", borderRadius: 6, paddingHorizontal: 12, paddingVertical: 10 },
  pendingText: { fontSize: 12, color: "#8B6508", lineHeight: 17 },
  infoStrip: { flexDirection: "row", alignItems: "center", gap: 8, marginHorizontal: 14, marginBottom: 14, backgroundColor: "#EEF4FB", borderRadius: 6, paddingHorizontal: 12, paddingVertical: 10 },
  infoText: { flex: 1, fontSize: 12, color: "#1f2937", lineHeight: 17 },
  confirmedBanner: { flexDirection: "row", alignItems: "flex-start", gap: 8, margin: 14, marginTop: 0, backgroundColor: "#ECFDF3", borderWidth: 1, borderColor: "#ABEFC6", borderRadius: 8, padding: 12 },
  confirmedTitle: { fontSize: 12, fontWeight: "700", color: "#027A48", lineHeight: 18 },
  confirmedSub: { fontSize: 12, color: "#475467", marginTop: 2, lineHeight: 18 },
  pressed: { opacity: 0.75 },
  overlay: { position: "absolute", top: 0, left: 0, right: 0, bottom: 0, zIndex: 10, backgroundColor: "rgba(255,255,255,0.85)", alignItems: "center", justifyContent: "center", gap: 8 },
  trackBox: { marginHorizontal: 14, marginBottom: 14, borderWidth: 1, borderColor: "#e5e7eb", borderRadius: 8, padding: 12, backgroundColor: "#F9FAFB" },
  trackTitle: { fontSize: 13, fontWeight: "700", color: "#111827" },
  trackHelp: { fontSize: 12, color: "#6b7280", marginTop: 2, marginBottom: 10, lineHeight: 17 },
  trackRow: { flexDirection: "row", flexWrap: "wrap", gap: 12 },
  trackCol: { flex: 1, minWidth: 200 },
  trackLabel: { fontSize: 12, fontWeight: "600", color: "#374151", marginBottom: 4 },
  trackInput: { borderWidth: 1, borderColor: "#d1d5db", borderRadius: 6, paddingHorizontal: 10, paddingVertical: 8, fontSize: 13, color: "#111827", backgroundColor: "#ffffff" },
  trackError: { fontSize: 12, color: "#B42318", marginTop: 8 },
  trackBtn: { alignSelf: "flex-start", marginTop: 12, backgroundColor: "#034895", borderRadius: 100, paddingHorizontal: 16, paddingVertical: 8 },
  trackBtnText: { fontSize: 12, fontWeight: "700", color: "#ffffff" },
  centred: { alignItems: "center" as any, justifyContent: "center" as any, paddingVertical: 40, gap: 10 },
  loadingText: { fontSize: 13, color: "#6b7280", marginTop: 8 },
  emptyText: { fontSize: 15, fontWeight: "700", color: "#6b7280", marginTop: 8 },
  emptySubText: { fontSize: 12, color: "#9ca3af", textAlign: "center" as any, maxWidth: 360, lineHeight: 18 },
  errorWrap: { flexDirection: "column" as any, alignItems: "center" as any, gap: 8, paddingVertical: 24 },
  errorText: { fontSize: 13, color: "#b45309", textAlign: "center" as any, maxWidth: 360 },
  retryBtn: { marginTop: 4, paddingHorizontal: 16, paddingVertical: 7, borderRadius: 7, borderWidth: 1, borderColor: "#d1d5db", backgroundColor: "#ffffff" },
  retryText: { fontSize: 13, fontWeight: "600", color: "#374151" },
});

export default ShippingVendorReceiptCard;
